import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import {
  Plus,
  Trash2,
  Globe,
  Database,
  Shield,
  Search,
  ChevronDown,
  Edit2,
  ArrowLeft,
  Code,
  Wrench,
  Info,
  Play,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  X,
  Copy,
  Cpu,
  Layers,
  KeyRound
} from "lucide-react";
import apiClient from "../utils/api";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { useToast } from "../context/ToastContext";
import PageHeader from "../components/PageHeader";

const CustomSelect = ({ value, options, onChange, label, className = "", disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value) || options[0];

  return (
    <div className={`relative ${className} ${disabled ? "opacity-50 pointer-events-none" : ""} ${isOpen ? "z-[50]" : "z-10"}`} ref={containerRef}>
      {label && <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 ml-1">{label}</label>}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-2.5 text-[11px] border rounded-xl transition-all duration-200 font-bold uppercase tracking-wider h-[38px] ${isOpen ? 'bg-white border-gray-900 ring-4 ring-gray-900/5 shadow-sm' : 'bg-gray-50 border-gray-100 text-gray-700 hover:bg-white hover:border-gray-200'
          }`}
      >
        <span className="truncate">{selectedOption.label}</span>
        <ChevronDown size={14} className={`text-gray-400 transition-transform duration-300 flex-shrink-0 ml-2 ${isOpen ? 'rotate-180 text-gray-900' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 w-full mt-2 bg-white border border-gray-100 rounded-xl shadow-xl z-[100] overflow-hidden animate-slideIn">
          {options.map((opt) => (
            <div
              key={opt.value}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
              className={`px-4 py-2.5 text-[11px] font-bold cursor-pointer transition-colors ${value === opt.value
                  ? "bg-gray-900 text-white"
                  : "text-gray-600 hover:bg-gray-50"
                }`}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ParameterRow = ({ parameter, path, onUpdate, onRemove, level = 0 }) => {
  const isObject = parameter.type === "object";
  const isArray = parameter.type === "array";

  const handleUpdate = (updates) => {
    onUpdate(path, { ...parameter, ...updates });
  };

  const addSubProperty = () => {
    const newProperties = [...(parameter.properties || []), {
      name: "",
      type: "string",
      location: "body",
      value_type: "llm",
      required: true,
      description: ""
    }];
    handleUpdate({ properties: newProperties });
  };

  const addArrayProperty = () => {
    const newItems = parameter.items || { type: "object", properties: [] };
    const newProperties = [...(newItems.properties || []), {
      name: "",
      type: "string",
      location: "body",
      value_type: "llm",
      required: true,
      description: ""
    }];
    handleUpdate({ items: { ...newItems, properties: newProperties } });
  };

  return (
    <div className={`space-y-6 ${level > 0 ? "ml-8 pl-8 border-l-2 border-gray-100 mt-6" : ""}`}>
      <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm hover:shadow-xl transition-all animate-slideIn group relative">
        <div className="space-y-6 relative z-10 hover:z-[30] focus-within:z-[40] transition-all">
          {/* Row 1: Core Identity */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-5">
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 ml-1">Identifier Name</label>
              <input
                placeholder="e.g. user_id"
                value={parameter.name}
                onChange={(e) => handleUpdate({ name: e.target.value })}
                className="w-full px-5 py-2.5 text-sm border border-gray-100 rounded-2xl bg-gray-50 focus:outline-none focus:ring-8 focus:ring-gray-900/5 focus:border-gray-900 focus:bg-white transition-all h-[42px] font-bold text-gray-900"
              />
            </div>
            <div className="md:col-span-3">
              <CustomSelect
                label="Type"
                value={parameter.type}
                onChange={(val) => handleUpdate({
                  type: val,
                  properties: val === 'object' ? [] : undefined,
                  location: (val === 'object' && parameter.location === 'path') ? 'body' : parameter.location
                })}
                options={[
                  { label: "String", value: "string" },
                  { label: "Number", value: "number" },
                  { label: "Integer", value: "integer" },
                  { label: "Boolean", value: "boolean" },
                  { label: "Object", value: "object" },
                  { label: "Array", value: "array" }
                ]}
              />
            </div>
            <div className="md:col-span-2">
              <CustomSelect
                label="Location"
                value={parameter.location || "body"}
                onChange={(val) => handleUpdate({ location: val })}
                options={[
                  { label: "Body", value: "body" },
                  { label: "Query", value: "query" },
                  ...(isObject ? [] : [{ label: "Path", value: "path" }])
                ]}
              />
            </div>
            <div className="md:col-span-2">
              <CustomSelect
                label="Extraction"
                value={isObject ? "llm" : (parameter.value_type || "llm")}
                disabled={isObject}
                onChange={(val) => handleUpdate({ value_type: val })}
                options={[
                  { label: "LLM", value: "llm" },
                  { label: "Constant Value", value: "constant" }
                ]}
              />
            </div>
          </div>

          {/* Row 2: Logic & Instructions */}
          {!isObject && (
            <div>
              {parameter.value_type === "constant" ? (
                <div className="animate-fadeIn">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 ml-1">
                    Constant Value (Injected Automatically)
                  </label>
                  <input
                    placeholder="Enter the fixed value to send to the API..."
                    value={parameter.constant_value || ""}
                    onChange={(e) => handleUpdate({ constant_value: e.target.value })}
                    className="w-full px-5 py-2.5 text-xs border border-gray-100 rounded-2xl bg-gray-50 focus:outline-none focus:ring-8 focus:ring-gray-900/5 focus:border-gray-900 focus:bg-white transition-all h-[42px] font-medium text-gray-900"
                  />
                </div>
              ) : (
                <div className="animate-fadeIn">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 ml-1">
                    Description (Instruction for AI)
                  </label>
                  <textarea
                    placeholder="Explain to the AI exactly how to extract or use this value..."
                    value={parameter.description || ""}
                    onChange={(e) => handleUpdate({ description: e.target.value })}
                    rows={2}
                    className="w-full px-5 py-3 text-xs border border-gray-100 rounded-2xl bg-gray-50 focus:outline-none focus:ring-8 focus:ring-gray-900/5 focus:border-gray-900 focus:bg-white transition-all font-medium resize-y min-h-[42px] leading-relaxed"
                  />
                </div>
              )}
            </div>
          )}

          <div className="pt-2 flex items-center justify-between border-t border-gray-50 mt-2">
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-3 bg-gray-50 px-4 py-2 rounded-xl border border-gray-100">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Required</span>
                <button
                  type="button"
                  onClick={() => handleUpdate({ required: !parameter.required })}
                  className={`w-10 h-5 rounded-full transition-all relative ${parameter.required ? "bg-gray-900" : "bg-gray-200"}`}
                >
                  <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${parameter.required ? "left-6" : "left-1"}`} />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {isObject && (
                <button
                  type="button"
                  onClick={addSubProperty}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-all font-bold text-[9px] uppercase tracking-widest border border-blue-100/50"
                >
                  <Plus size={12} /> Add Property
                </button>
              )}
              {isArray && (
                <button
                  type="button"
                  onClick={addArrayProperty}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-all font-bold text-[9px] uppercase tracking-widest border border-blue-100/50"
                >
                  <Plus size={12} /> Add Array Item Property
                </button>
              )}
              <button
                type="button"
                onClick={() => onRemove(path)}
                className="w-9 h-9 flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {isObject && (
        <div className="animate-fadeIn">
          {parameter.properties?.map((prop, idx) => (
            <ParameterRow
              key={idx}
              parameter={prop}
              path={[...path, 'properties', idx]}
              onUpdate={onUpdate}
              onRemove={onRemove}
              level={level + 1}
            />
          ))}
          <button
            type="button"
            onClick={addSubProperty}
            className="ml-8 mt-3 text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-900 transition-all flex items-center gap-2 py-2 px-3 rounded-xl hover:bg-gray-50 border border-transparent hover:border-gray-100"
          >
            <Plus size={14} /> Add Property to {parameter.name || 'Object'}
          </button>
        </div>
      )}
      {isArray && (
        <div className="animate-fadeIn">
          {parameter.items?.properties?.map((prop, idx) => (
            <ParameterRow
              key={idx}
              parameter={prop}
              path={[...path, 'items', 'properties', idx]}
              onUpdate={onUpdate}
              onRemove={onRemove}
              level={level + 1}
            />
          ))}
          <button
            type="button"
            onClick={addArrayProperty}
            className="ml-8 mt-3 text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-900 transition-all flex items-center gap-2 py-2 px-3 rounded-xl hover:bg-gray-50 border border-transparent hover:border-gray-100"
          >
            <Plus size={14} /> Add Array Item Property to {parameter.name || 'Array'}
          </button>
        </div>
      )}
    </div>
  );
};

const ApiToolsManagement = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTool, setEditingTool] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isJsonMode, setIsJsonMode] = useState(false);
  const [jsonContent, setJsonContent] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [toolToDelete, setToolToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [testInputs, setTestInputs] = useState({});
  const [arrayLengths, setArrayLengths] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [isTestLoading, setIsTestLoading] = useState(false);
  const [isRawRequestOpen, setIsRawRequestOpen] = useState(false);
  const [jsonError, setJsonError] = useState(null);
  const [initialParams, setInitialParams] = useState([]);

  // MCP State
  const [isMcpMode, setIsMcpMode] = useState(false);
  const [mcpServers, setMcpServers] = useState([]);
  const [isAddingMcp, setIsAddingMcp] = useState(false);
  const [mcpDiscovery, setMcpDiscovery] = useState({ url: "", headers: "{}", tools: [] });
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [uiError, setUiError] = useState(null);

  const emptyTool = {
    name: "",
    description: "",
    url: "",
    method: "GET",
    headers: "{}",
    parameters: [],
    otp_enabled: false,
    otp_config: {
      digits: 4,
      verify_url: "",
      verify_headers: {},
      verify_body_template: {
        otp: "{{otp}}"
      },
      context_mappings: {},
      verify_success: {
        type: "json_path",
        path: "success",
        expected: true
      },
      max_attempts: 3
    },
    is_active: true
  };

  const fetchTools = useCallback(async () => {
    try {
      const response = await apiClient.getTools();
      const toolsArray = Array.isArray(response) ? response : (response?.tools || []);
      setTools(toolsArray);
    } catch (error) {
      console.error("Error fetching tools:", error);
      setTools([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMcpServers = useCallback(async () => {
    try {
      const response = await apiClient.get("/mcp/servers", {
        headers: { "X-AI-Agent-ID": activeAiAgentId }
      });
      setMcpServers(response || []);
    } catch (error) {
      console.error("Error fetching MCP servers:", error);
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    if (activeAiAgentId) {
      fetchTools();
      fetchMcpServers();
    }
  }, [activeAiAgentId, fetchTools, fetchMcpServers]);

  // Real-time JSON Validation & Strict Schema Check
  useEffect(() => {
    if (isJsonMode && jsonContent) {
      try {
        const parsed = JSON.parse(jsonContent);
        setJsonError(null);

        // 1. Root Level Validation
        const allowedRootKeys = ['name', 'description', 'method', 'url', 'headers', 'parameters', 'otp_enabled', 'otp_config', 'is_active'];
        Object.keys(parsed).forEach(key => {
          if (!allowedRootKeys.includes(key)) {
            throw new Error(`Unknown top-level property: "${key}". Available: ${allowedRootKeys.join(', ')}`);
          }
        });

        // 2. Global Safety Protection (Existing Parameter Stability)
        if (parsed.parameters && Array.isArray(parsed.parameters)) {
          const protectedFields = ['name', 'type', 'location', 'required'];
          const validTypes = ['string', 'number', 'integer', 'boolean', 'object', 'array'];
          const validLocations = ['query', 'path', 'body'];
          const validValueTypes = ['llm', 'constant'];

          const validateParamRecursive = (param, pNum, isNested = false) => {
            // Check for Unknown Parameter Keys
            const allowedKeys = ['name', 'type', 'location', 'value_type', 'required', 'constant_value', 'description', 'properties', 'items'];
            Object.keys(param).forEach(k => {
              if (!allowedKeys.includes(k)) {
                throw new Error(`${isNested ? 'Nested parameter' : `Parameter #${pNum}`}: Unknown property "${k}" found.`);
              }
            });

            // Validate Values
            if (param.type && !validTypes.includes(param.type))
              throw new Error(`${isNested ? 'Nested parameter' : `Parameter #${pNum}`}: "${param.type}" is invalid. Must be one of: ${validTypes.join(', ')}`);

            if (!isNested && param.location && !validLocations.includes(param.location))
              throw new Error(`Parameter #${pNum}: "${param.location}" is invalid. Must be 'query', 'path', or 'body'`);

            if (param.value_type && !validValueTypes.includes(param.value_type))
              throw new Error(`${isNested ? 'Nested parameter' : `Parameter #${pNum}`}: "value_type" must be 'llm' or 'constant'`);

            if (param.required !== undefined && typeof param.required !== 'boolean')
              throw new Error(`${isNested ? 'Nested parameter' : `Parameter #${pNum}`}: "required" must be a boolean (true or false)`);

            if (param.properties !== undefined) {
              if (!Array.isArray(param.properties)) {
                throw new Error(`${isNested ? 'Nested parameter' : `Parameter #${pNum}`}: "properties" must be an array`);
              }
              param.properties.forEach((subParam, idx) => {
                validateParamRecursive(subParam, `${pNum}.${idx + 1}`, true);
              });
            }

            if (param.items !== undefined) {
              if (typeof param.items !== 'object' || Array.isArray(param.items)) {
                throw new Error(`${isNested ? 'Nested parameter' : `Parameter #${pNum}`}: "items" must be an object`);
              }
              validateParamRecursive(param.items, `${pNum}.items`, true);
            }
          };

          parsed.parameters.forEach((current, i) => {
            const pNum = i + 1;

            validateParamRecursive(current, pNum, false);

            // Protection validation for existing items
            if (i < initialParams.length) {
              const original = initialParams[i];
              protectedFields.forEach(field => {
                if (current[field] !== original[field]) {
                  throw new Error(`Safety Violation: Field "${field}" in existing parameter #${pNum} cannot be modified from "${original[field]}" to "${current[field]}".`);
                }
              });
            }
          });
        }
      } catch (err) {
        // Strict Error Reporting: Ensure no truncation by browser or logic
        let msg = err.message || "Unknown validation exception";

        // Custom formatting for common JSON syntax errors to make them readable
        if (msg.includes("JSON.parse")) {
          msg = `JSON Syntax Violation: ${msg.replace("JSON.parse: ", "")}`;
        }

        setJsonError(msg);
      }
    } else {
      setJsonError(null);
    }
  }, [jsonContent, isJsonMode, initialParams]);



  const handleDiscoverMcp = async () => {
    if (!mcpDiscovery.url) return;
    setIsDiscovering(true);
    try {
      const headers = typeof mcpDiscovery.headers === 'string'
        ? JSON.parse(mcpDiscovery.headers || "{}")
        : mcpDiscovery.headers;

      const response = await apiClient.post("/mcp/discover", {
        url: mcpDiscovery.url,
        headers: headers
      });
      setMcpDiscovery(prev => ({
        ...prev,
        tools: (response.tools || []).map(name => ({ tool_name: name, is_active: true }))
      }));
      setUiError(null);
    } catch (error) {
      setUiError("Discovery Failure: " + (error.response?.data?.detail || error.message));
    } finally {
      setIsDiscovering(false);
    }
  };

  const handleSaveMcp = async () => {
    try {
      const headers = typeof mcpDiscovery.headers === 'string'
        ? JSON.parse(mcpDiscovery.headers || "{}")
        : mcpDiscovery.headers;

      await apiClient.post("/mcp/servers", {
        url: mcpDiscovery.url,
        headers: headers,
        tools: mcpDiscovery.tools.filter(t => t.is_active).map(t => t.tool_name)
      });
      setIsAddingMcp(false);
      setMcpDiscovery({ url: "", headers: "{}", tools: [] });
      setUiError(null);
      fetchMcpServers();
    } catch (error) {
      setUiError("Synchronization Error: " + (error.response?.data?.detail || error.message));
    }
  };

  const toggleMcpServer = async (serverId, currentStatus) => {
    try {
      await apiClient.put(`/mcp/servers/${serverId}`, { is_active: !currentStatus });
      fetchMcpServers();
    } catch (error) {
      console.error("Error toggling MCP server:", error);
    }
  };

  const toggleMcpTool = async (toolId, currentStatus) => {
    try {
      await apiClient.put(`/mcp/tools/${toolId}`, { is_active: !currentStatus });
      fetchMcpServers();
    } catch (error) {
      console.error("Error toggling MCP tool:", error);
    }
  };

  const [mcpServerToDelete, setMcpServerToDelete] = useState(null);
  const [isDeletingMcp, setIsDeletingMcp] = useState(false);

  const deleteMcpServer = (serverId) => {
    setMcpServerToDelete(serverId);
  };

  const confirmDeleteMcp = async () => {
    if (!mcpServerToDelete) return;
    setIsDeletingMcp(true);
    try {
      await apiClient.delete(`/mcp/servers/${mcpServerToDelete}`);
      fetchMcpServers();
      setMcpServerToDelete(null);
    } catch (error) {
      console.error("Error deleting MCP server:", error);
    } finally {
      setIsDeletingMcp(false);
    }
  };

  const handleSave = async (toolData) => {
    try {
      let finalizedData = { ...toolData };

      if (isJsonMode) {
        try {
          finalizedData = JSON.parse(jsonContent);
        } catch (e) {
          setUiError("Critical: Invalid JSON structure. Please rectify syntax.");
          return;
        }
      }

      // Headers must be a dictionary
      if (typeof finalizedData.headers === 'string') {
        try {
          finalizedData.headers = JSON.parse(finalizedData.headers || '{}');
        } catch (e) {
          setUiError("Header Violation: Invalid JSON in headers.");
          return;
        }
      }

      // Parameters must be a list
      if (typeof finalizedData.parameters === 'string') {
        try {
          finalizedData.parameters = JSON.parse(finalizedData.parameters || '[]');
        } catch (e) {
          setUiError("Parameter Violation: Invalid JSON in parameters.");
          return;
        }
      }

      finalizedData.otp_enabled = Boolean(finalizedData.otp_enabled);
      if (finalizedData.otp_config) {
        let conf = finalizedData.otp_config;
        if (typeof conf === 'string') {
          try { conf = JSON.parse(conf); } catch (e) { conf = {}; }
        }
        if (typeof conf?.verify_headers === 'string') {
          try { conf.verify_headers = JSON.parse(conf.verify_headers || '{}'); } catch (e) { conf.verify_headers = {}; }
        }
        if (typeof conf?.context_mappings === 'string') {
          try { conf.context_mappings = JSON.parse(conf.context_mappings || '{}'); } catch (e) { conf.context_mappings = {}; }
        }
        if (typeof conf?.verify_body_template === 'string') {
          try { conf.verify_body_template = JSON.parse(conf.verify_body_template); } catch (e) {}
        }
        finalizedData.otp_config = conf;
      }

      if (editingTool?.id) {
        await apiClient.updateTool(editingTool.id, finalizedData);
        showToast("Capability synchronized successfully", "success");
      } else {
        const response = await apiClient.createTool(finalizedData);
        finalizedData.id = response.id;
        showToast("Capability launched successfully", "success");
      }

      // Update local state and keep the editor open on the visual UI
      await fetchTools();
      setEditingTool({
        ...finalizedData,
        parameters: typeof finalizedData.parameters === 'string' ? JSON.parse(finalizedData.parameters || '[]') : (finalizedData.parameters || [])
      });
      setIsAdding(false);
      setIsJsonMode(false);
      setUiError(null);
    } catch (err) {
      console.error("Save Error:", err);
      setUiError("Storage Failure: " + err.message);
    }
  };

  const handleDelete = (id) => {
    setToolToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!toolToDelete) return;
    setIsDeleting(true);
    try {
      await apiClient.deleteTool(toolToDelete);
      fetchTools();
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting tool:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggle = async (tool) => {
    try {
      const newStatus = !tool.is_active;
      await apiClient.toggleTool(tool.id, newStatus);
      showToast(`Extension ${newStatus ? 'activated' : 'dismantled'} successfully`, "success");
      fetchTools();
    } catch (error) {
      console.error("Error toggling tool status:", error);
    }
  };

  const handleEdit = (tool) => {
    let params = tool.parameters;
    if (typeof params === 'string') {
      try { params = JSON.parse(params); } catch { params = []; }
    }

    setEditingTool({
      ...tool,
      url: tool.url,
      parameters: params || [],
      headers: tool.headers || {}
    });
    setIsJsonMode(false); // Always start in Visual UI mode
    setIsAdding(false);
    setTestInputs({});
    setTestResult(null);
  };

  const handleUpdateParameter = (path, updated) => {
    const newParams = JSON.parse(JSON.stringify(editingTool.parameters || []));
    let current = newParams;
    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }
    current[path[path.length - 1]] = updated;
    setEditingTool({ ...editingTool, parameters: newParams });
  };

  const handleRemoveParameter = (path) => {
    const newParams = JSON.parse(JSON.stringify(editingTool.parameters || []));
    let current = newParams;
    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }
    current.splice(path[path.length - 1], 1);
    setEditingTool({ ...editingTool, parameters: newParams });
  };

  const addParameter = () => {
    const currentTool = editingTool || emptyTool;
    const newParams = [...(currentTool.parameters || []), {
      name: "",
      type: "string",
      location: "body",
      value_type: "llm",
      required: true,
      description: ""
    }];
    setEditingTool({ ...currentTool, parameters: newParams });
  };

  const handleMethodChange = (newMethod) => {
    const currentTool = editingTool || emptyTool;
    let newParams = [...(currentTool.parameters || [])];

    if ((newMethod === "POST" || newMethod === "PUT") && newParams.length === 0) {
      newParams.push({
        name: "",
        type: "string",
        location: "body",
        value_type: "llm",
        required: true,
        description: ""
      });
    }

    setEditingTool({ ...currentTool, method: newMethod, parameters: newParams });
  };

  const getMethodColor = (method) => {
    switch (method?.toUpperCase()) {
      case 'GET': return 'bg-blue-50 text-blue-600 border-blue-100';
      case 'POST': return 'bg-blue-50 text-blue-600 border-blue-100';
      case 'PUT': return 'bg-amber-50 text-amber-600 border-amber-100';
      case 'DELETE': return 'bg-red-50 text-red-600 border-red-100';
      default: return 'bg-gray-50 text-gray-600 border-gray-100';
    }
  };

  const getHostname = (url) => {
    try {
      return new URL(url).hostname;
    } catch {
      return url || "N/A";
    }
  };

  const filteredTools = (Array.isArray(tools) ? tools : []).filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const serializeTool = (tool) => {
    const formatted = {
      name: tool.name,
      description: tool.description,
      method: tool.method,
      url: tool.url,
      headers: typeof tool.headers === 'string' ? JSON.parse(tool.headers || '{}') : (tool.headers || {}),
      parameters: typeof tool.parameters === 'string' ? JSON.parse(tool.parameters || '[]') : (tool.parameters || []),
      otp_enabled: Boolean(tool.otp_enabled),
      otp_config: tool.otp_config || {},
      is_active: tool.is_active
    };
    return JSON.stringify(formatted, null, 2);
  };

  const handleToggleJsonMode = () => {
    if (!isJsonMode) {
      // Switching TO JSON mode
      const tool = editingTool || emptyTool;
      const content = serializeTool(tool);
      setJsonContent(content);

      // Store initial parameters for comparison/protection
      const params = typeof tool.parameters === 'string' ? JSON.parse(tool.parameters || '[]') : (tool.parameters || []);
      setInitialParams(params);

      setIsJsonMode(true);
    } else {
      // Switching TO Form mode
      if (jsonError) return; // Prevent switching if there are errors
      try {
        const parsed = JSON.parse(jsonContent);
        setEditingTool({
          ...editingTool, // Preserve ID and other internal metadata
          ...parsed,
          headers: typeof parsed.headers === 'string' ? parsed.headers : JSON.stringify(parsed.headers || {}),
          parameters: parsed.parameters || []
        });
        setIsJsonMode(false);
      } catch (e) {
        showToast("Cannot switch to Form view: Invalid JSON content.", "error");
      }
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-4 border-gray-100 border-t-gray-900 rounded-full animate-spin mb-4" />
        <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">Loading Capabilities...</p>
      </div>
    );
  }

  const renderEditor = () => {
    const tool = editingTool || emptyTool;
    return (
      <div className="animate-fadeIn">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => { setEditingTool(null); setIsAdding(false); }}
            className="flex items-center gap-2 text-gray-400 hover:text-gray-900 transition-colors font-bold text-[10px] uppercase tracking-widest"
          >
            <ArrowLeft size={14} /> Back to Tools
          </button>
          <div className="flex items-center gap-3">
            {editingTool?.id && (
              <button
                onClick={() => {
                  const initialInputs = {};
                  const initialLengths = {};
                  const initParams = (paramsList, parentPath = []) => {
                    paramsList.forEach(p => {
                      if (!p.name) return;
                      const fullPath = [...parentPath, p.name].join('.');
                      if (p.type === 'object') {
                        initParams(p.properties || [], [...parentPath, p.name]);
                      } else if (p.type === 'array') {
                        initialLengths[fullPath] = 1;
                        const subProperties = p.items?.properties || [];
                        if (subProperties.length > 0) {
                          initParams(subProperties, [...parentPath, p.name, 0]);
                        } else {
                          initialInputs[`${fullPath}.0`] = '';
                        }
                      } else {
                        initialInputs[fullPath] = p.value_type === 'constant' ? (p.constant_value || '') : '';
                      }
                    });
                  };
                  initParams(tool.parameters || []);
                  setTestInputs(initialInputs);
                  setArrayLengths(initialLengths);
                  setTestResult(null);
                  setIsTestModalOpen(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-600 rounded-xl hover:bg-gray-100 transition-all font-bold text-[10px] uppercase tracking-widest border border-gray-100"
              >
                <Play size={14} /> Test Capability
              </button>
            )}
            {!isJsonMode ? (
              <button
                onClick={handleToggleJsonMode}
                className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-600 rounded-xl hover:bg-gray-100 transition-all font-bold text-[10px] uppercase tracking-widest border border-gray-100"
              >
                <Code size={14} /> Edit as JSON
              </button>
            ) : null}
            {editingTool?.id && (
              <button
                onClick={() => {
                  setEditingTool(null);
                  setIsAdding(true);
                  setIsJsonMode(false);
                  setUiError(null);
                }}
                className="flex items-center gap-2 px-6 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-black transition-all font-bold text-[10px] uppercase tracking-widest shadow-lg shadow-gray-200 border border-transparent active:scale-95"
              >
                <Plus size={14} /> Add New Capability
              </button>
            )}
            <button
              disabled={!tool.name || (!isJsonMode && !tool.url)}
              onClick={() => handleSave(tool)}
              className="flex items-center gap-2 px-8 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-black transition-all font-bold text-[10px] uppercase tracking-widest shadow-xl shadow-gray-200 disabled:opacity-50 disabled:shadow-none border border-transparent active:scale-95"
            >
              <Wrench size={14} /> {editingTool?.id ? (isJsonMode ? "Synchronize Code" : "Synchronize Capability") : "Launch Extension"}
            </button>
          </div>
        </div>

        {isJsonMode ? (
          <div className="animate-slideUp mt-4">
            <div className="bg-white rounded-[32px] border border-gray-100 shadow-2xl p-8 overflow-hidden relative group">
              <div className="flex items-center justify-between mb-6">
                <div className="space-y-1">
                  <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-gray-900 text-white flex items-center justify-center">
                      <Code size={16} />
                    </div>
                    Neural Configuration Code
                  </h2>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest ml-11">
                    Edit tool configuration as JSON. Maintain correct structure for synchronization.
                  </p>
                </div>
              </div>

              <div className="relative rounded-[24px] overflow-hidden border border-gray-100 bg-gray-50 shadow-inner">
                <div className="absolute left-0 top-0 bottom-0 w-12 bg-gray-100/50 border-r border-gray-100 flex flex-col items-center pt-4 select-none">
                  {[...Array(25)].map((_, i) => (
                    <span key={i} className="text-[10px] font-mono text-gray-300 mb-1">{i + 1}</span>
                  ))}
                </div>
                <textarea
                  value={jsonContent}
                  onChange={(e) => setJsonContent(e.target.value)}
                  className="w-full pl-16 pr-6 py-4 text-[13px] font-mono bg-transparent focus:outline-none min-h-[500px] text-gray-700 leading-relaxed resize-none"
                  spellCheck="false"
                />
              </div>

              <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-50">
                <div className="flex-1">
                  {(jsonError || uiError) ? (
                    <div className="flex items-start gap-4 text-red-600 animate-fadeIn bg-red-50/30 p-5 rounded-[20px] border border-red-100/50">
                      <div className="w-2 h-2 rounded-full bg-red-600 animate-pulse mt-1.5 flex-shrink-0" />
                      <div className="space-y-1">
                        <p className="text-[10px] font-black uppercase tracking-widest mb-1">Infrastructure Alert</p>
                        <p className="text-[13px] font-bold leading-relaxed break-words whitespace-pre-wrap">
                          {jsonError || uiError}
                        </p>
                        <p className="text-[11px] text-red-500/70 font-medium leading-relaxed max-w-2xl">
                          Ensure all protocol specifications follow strict JSON standards. Immutable primitive fields (name, type, location, etc.) must match their origin definitions.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-end w-full">
                      <button
                        onClick={handleToggleJsonMode}
                        className="flex items-center gap-2 px-10 py-3.5 bg-gray-900 text-white rounded-2xl hover:bg-black transition-all font-bold text-[11px] uppercase tracking-widest shadow-xl shadow-gray-200 active:scale-95"
                      >
                        <Database size={16} /> Visual Editor
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                {/* Visual Form Components */}
                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8">
                  <h2 className="text-sm font-bold text-gray-900 uppercase tracking-widest mb-6 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-900 text-white flex items-center justify-center">
                      <Globe size={16} />
                    </div>
                    Endpoint Configuration
                  </h2>

                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                      <div className="md:col-span-1">
                        <CustomSelect
                          label="Method"
                          value={tool.method}
                          onChange={handleMethodChange}
                          options={[
                            { label: "GET", value: "GET" },
                            { label: "POST", value: "POST" },
                            { label: "PUT", value: "PUT" },
                            { label: "DELETE", value: "DELETE" }
                          ]}
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Service URL</label>
                        <input
                          placeholder="https://api.your-service.com/endpoint"
                          value={tool.url}
                          onChange={(e) => setEditingTool({ ...tool, url: e.target.value })}
                          className="w-full px-4 py-2 text-xs border border-gray-100 rounded-xl bg-gray-50 focus:outline-none focus:ring-4 focus:ring-gray-900/5 focus:border-gray-900 focus:bg-white transition-all h-[42px] font-medium"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Tool Capability Name</label>
                      <input
                        placeholder="e.g. Fetch Order Status"
                        value={tool.name}
                        onChange={(e) => setEditingTool({ ...tool, name: e.target.value })}
                        className="w-full px-4 py-2 text-sm border border-gray-100 rounded-xl bg-gray-50 focus:outline-none focus:ring-4 focus:ring-gray-900/5 focus:border-gray-900 focus:bg-white transition-all h-[48px] font-bold text-gray-900"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Detailed Description (for AI Context)</label>
                      <textarea
                        placeholder="Explain exactly what this tool does so the AI knows when to trigger it..."
                        value={tool.description}
                        rows={3}
                        onChange={(e) => setEditingTool({ ...tool, description: e.target.value })}
                        className="w-full px-4 py-3 text-xs border border-gray-100 rounded-xl bg-gray-50 focus:outline-none focus:ring-4 focus:ring-gray-900/5 focus:border-gray-900 focus:bg-white transition-all font-medium resize-none"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-8">
                {/* Headers Configuration */}
                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8">
                  <h2 className="text-sm font-bold text-gray-900 uppercase tracking-widest mb-6 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-900 text-white flex items-center justify-center">
                      <Shield size={16} />
                    </div>
                    Custom Headers
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 ml-1 font-mono text-[9px]">JSON Format (Required)</label>
                      <textarea
                        placeholder='{
      "Content-Type": "application/json",
      "Authorization": "Bearer YOUR_TOKEN"
    }'
                        value={typeof tool.headers === 'string' ? tool.headers : JSON.stringify(tool.headers || {}, null, 2)}
                        onChange={(e) => setEditingTool({ ...tool, headers: e.target.value })}
                        rows={6}
                        className="w-full px-4 py-3 text-[11px] border border-gray-100 rounded-xl bg-gray-50 focus:outline-none focus:ring-4 focus:ring-gray-900/5 focus:border-gray-900 focus:bg-white transition-all font-mono resize-none shadow-inner"
                      />
                      <div className="flex items-start gap-2 mt-3 ml-1 bg-blue-50/50 p-3 rounded-xl border border-blue-100/30">
                        <Info size={14} className="text-blue-500 mt-0.5" />
                        <p className="text-[10px] text-black leading-relaxed font-medium">
                          Important: Ensure all keys and values are wrapped in <strong>double quotes</strong> (e.g., "Key": "Value").
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Unified Payload Schema View */}
            <div className="mt-8 bg-white rounded-3xl border border-gray-100 shadow-sm p-8 animate-slideUp">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-sm font-bold text-gray-900 uppercase tracking-widest flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gray-900 text-white flex items-center justify-center">
                    <Database size={16} />
                  </div>
                  Payload Schema Configuration
                </h2>
                <button
                  type="button"
                  onClick={addParameter}
                  className="flex items-center gap-2 px-6 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-black transition-all font-bold text-[10px] uppercase tracking-widest shadow-lg shadow-gray-200"
                >
                  <Plus size={14} /> Add Identifier
                </button>
              </div>

              <div className="space-y-4">
                {tool.parameters?.length > 0 ? (
                  tool.parameters.map((param, index) => (
                    <ParameterRow
                      key={index}
                      parameter={param}
                      path={[index]}
                      onUpdate={handleUpdateParameter}
                      onRemove={handleRemoveParameter}
                    />
                  ))
                ) : (
                  <div className="p-12 text-center rounded-3xl border-2 border-dashed border-gray-50">
                    <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Code size={20} className="text-gray-300" />
                    </div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">No parameters defined yet</p>
                    <p className="text-[10px] text-gray-300 mt-1 max-w-[200px] mx-auto">Define the data your API requires from the LLM.</p>
                  </div>
                )}
              </div>
            </div>

            {/* OTP Verification Configuration (Dynamic External Validation) */}
            <div className="mt-8 bg-white rounded-3xl border border-gray-100 shadow-sm p-8 animate-slideUp">
              <div className="flex items-center justify-between pb-6 border-b border-gray-100 mb-6">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                    tool.otp_enabled ? "bg-emerald-600 text-white shadow-md shadow-emerald-200" : "bg-gray-100 text-gray-500"
                  }`}>
                    <KeyRound size={16} />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-gray-900 uppercase tracking-widest flex items-center gap-2">
                      OTP Verification
                      {tool.otp_enabled && (
                        <span className="text-[9px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-bold border border-emerald-200">ACTIVE</span>
                      )}
                    </h2>
                    <p className="text-[11px] text-gray-400 font-medium">
                      When triggered by the AI agent, the chat input transforms into PIN digit boxes and validates against your external endpoint.
                    </p>
                  </div>
                </div>

                {/* Switch Toggle */}
                <label className="relative inline-flex items-center cursor-pointer group">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={Boolean(tool.otp_enabled)}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setEditingTool({
                        ...tool,
                        otp_enabled: enabled,
                        otp_config: tool.otp_config || {
                          digits: 4,
                          verify_url: "",
                          verify_headers: {},
                          verify_body_template: { otp: "{{otp}}" },
                          context_mappings: {},
                          verify_success: {
                            type: "json_path",
                            path: "success",
                            expected: true
                          },
                          max_attempts: 3
                        }
                      });
                    }}
                  />
                  <div className="w-12 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                </label>
              </div>

              {tool.otp_enabled && (
                <div className="space-y-6 animate-fadeIn">
                  {/* Row 1: Digits & Max Attempts */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 bg-gray-50/70 p-5 rounded-2xl border border-gray-100">
                    <div>
                      <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1.5 ml-1">PIN Boxes Count (Number of Digits)</label>
                      <div className="flex gap-3">
                        {[4, 6].map(num => (
                          <button
                            key={num}
                            type="button"
                            onClick={() => setEditingTool({
                              ...tool,
                              otp_config: { ...(tool.otp_config || {}), digits: num }
                            })}
                            className={`flex-1 py-2.5 px-4 rounded-xl text-xs font-bold border transition-all ${
                              (tool.otp_config?.digits || 4) === num
                                ? "bg-gray-900 text-white border-gray-900 shadow-sm"
                                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                            }`}
                          >
                            {num} Digits {num === 4 ? "(Standard)" : ""}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1.5 ml-1">Maximum Allowed Attempts</label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={tool.otp_config?.max_attempts ?? 3}
                        onChange={(e) => setEditingTool({
                          ...tool,
                          otp_config: { ...(tool.otp_config || {}), max_attempts: parseInt(e.target.value, 10) || 3 }
                        })}
                        className="w-full px-4 py-2 text-xs border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-4 focus:ring-gray-900/5 focus:border-gray-900 transition-all h-[42px] font-bold text-gray-900"
                      />
                    </div>
                  </div>

                  {/* Verification Endpoint (POST ONLY) */}
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1.5 ml-1">
                      External Verification Endpoint URL <span className="text-rose-500">* (HTTPS POST only)</span>
                    </label>
                    <div className="relative">
                      <input
                        type="url"
                        placeholder="https://api.yourdomain.com/auth/verify-otp"
                        value={tool.otp_config?.verify_url || ""}
                        onChange={(e) => setEditingTool({
                          ...tool,
                          otp_config: { ...(tool.otp_config || {}), verify_url: e.target.value }
                        })}
                        className="w-full pl-20 pr-4 py-2 text-xs border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-gray-900/5 focus:border-gray-900 transition-all h-[46px] font-mono text-gray-900"
                      />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900 text-white text-[9px] font-bold rounded-md tracking-wider">
                        POST
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1 ml-1 font-medium">
                      All verification is strictly sent via HTTP POST to prevent sensitive OTP leakage into access logs.
                    </p>
                  </div>

                  {/* Verification Secret Headers */}
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1.5 ml-1">
                      Verification Secret Headers (JSON)
                    </label>
                    <textarea
                      rows={3}
                      placeholder='{\n  "Authorization": "Bearer YOUR_SECRET_KEY"\n}'
                      value={
                        typeof tool.otp_config?.verify_headers === 'string'
                          ? tool.otp_config.verify_headers
                          : JSON.stringify(tool.otp_config?.verify_headers || {}, null, 2)
                      }
                      onChange={(e) => {
                        setEditingTool({
                          ...tool,
                          otp_config: { ...(tool.otp_config || {}), verify_headers: e.target.value }
                        });
                      }}
                      className="w-full px-4 py-3 text-[11px] border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-gray-900/5 focus:border-gray-900 transition-all font-mono resize-none shadow-inner"
                    />
                    <p className="text-[10px] text-gray-400 mt-1 ml-1 font-medium">
                      🔒 Stored securely server-side. Never sent to the client browser.
                    </p>
                  </div>

                  {/* Verification Request Body Payload Template */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5 ml-1">
                      <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                        Verification Request Payload Template (JSON)
                      </label>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-gray-400 mr-1">Variables:</span>
                        {['{{otp}}', '{{phone}}', '{{verification_id}}', '{{session_id}}'].map(tag => (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => {
                              const currentTpl = typeof tool.otp_config?.verify_body_template === 'string'
                                ? tool.otp_config.verify_body_template
                                : JSON.stringify(tool.otp_config?.verify_body_template || { otp: "{{otp}}" }, null, 2);
                              const updated = currentTpl.includes(tag) ? currentTpl : currentTpl.replace(/\}$/, `  "${tag.replace(/[{}]/g, '')}": "${tag}"\n}`);
                              setEditingTool({
                                ...tool,
                                otp_config: { ...(tool.otp_config || {}), verify_body_template: updated }
                              });
                            }}
                            className="px-2 py-0.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-md text-[9px] font-mono font-bold border border-blue-200 transition-colors"
                          >
                            + {tag}
                          </button>
                        ))}
                      </div>
                    </div>
                    <textarea
                      rows={5}
                      placeholder='{\n  "otp": "{{otp}}",\n  "phone": "{{phone}}",\n  "verification_id": "{{verification_id}}"\n}'
                      value={
                        typeof tool.otp_config?.verify_body_template === 'string'
                          ? tool.otp_config.verify_body_template
                          : JSON.stringify(tool.otp_config?.verify_body_template || { otp: "{{otp}}" }, null, 2)
                      }
                      onChange={(e) => {
                        setEditingTool({
                          ...tool,
                          otp_config: { ...(tool.otp_config || {}), verify_body_template: e.target.value }
                        });
                      }}
                      className="w-full px-4 py-3 text-[11px] border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-gray-900/5 focus:border-gray-900 transition-all font-mono resize-none shadow-inner"
                    />
                  </div>

                  {/* Sender Context Mappings */}
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1.5 ml-1">
                      Response Context Extraction Mappings (JSON)
                    </label>
                    <textarea
                      rows={3}
                      placeholder='{\n  "verification_id": "response.data.id"\n}'
                      value={
                        typeof tool.otp_config?.context_mappings === 'string'
                          ? tool.otp_config.context_mappings
                          : JSON.stringify(tool.otp_config?.context_mappings || {}, null, 2)
                      }
                      onChange={(e) => {
                        setEditingTool({
                          ...tool,
                          otp_config: { ...(tool.otp_config || {}), context_mappings: e.target.value }
                        });
                      }}
                      className="w-full px-4 py-3 text-[11px] border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-gray-900/5 focus:border-gray-900 transition-all font-mono resize-none shadow-inner"
                    />
                    <p className="text-[10px] text-gray-400 mt-1 ml-1 font-medium">
                      Extract dynamic IDs from the tool's sender API response (e.g. mapping "verification_id" from "response.id") to use in the verification template above.
                    </p>
                  </div>

                  {/* Explicit Success Evaluation Rule */}
                  <div className="bg-gray-50/70 p-5 rounded-2xl border border-gray-100 space-y-4">
                    <label className="block text-[10px] font-bold text-gray-900 uppercase tracking-widest">
                      Success Evaluation Rule (Never relies blindly on HTTP 200)
                    </label>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Rule Type</label>
                        <select
                          value={tool.otp_config?.verify_success?.type || "json_path"}
                          onChange={(e) => setEditingTool({
                            ...tool,
                            otp_config: {
                              ...(tool.otp_config || {}),
                              verify_success: {
                                ...(tool.otp_config?.verify_success || {}),
                                type: e.target.value
                              }
                            }
                          })}
                          className="w-full px-3 py-2 text-xs border border-gray-200 rounded-xl bg-white font-bold text-gray-700 h-[38px]"
                        >
                          <option value="json_path">Match JSON Property Path</option>
                          <option value="boolean">Direct Boolean Response (true/false)</option>
                        </select>
                      </div>

                      {tool.otp_config?.verify_success?.type !== "boolean" && (
                        <div>
                          <label className="block text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">JSON Path</label>
                          <input
                            type="text"
                            placeholder="success or data.is_valid"
                            value={tool.otp_config?.verify_success?.path || "success"}
                            onChange={(e) => setEditingTool({
                              ...tool,
                              otp_config: {
                                ...(tool.otp_config || {}),
                                verify_success: {
                                  ...(tool.otp_config?.verify_success || {}),
                                  path: e.target.value
                                }
                              }
                            })}
                            className="w-full px-3 py-2 text-xs border border-gray-200 rounded-xl bg-white font-mono text-gray-800 h-[38px]"
                          />
                        </div>
                      )}

                      <div>
                        <label className="block text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Expected Value</label>
                        <input
                          type="text"
                          placeholder="true"
                          value={String(tool.otp_config?.verify_success?.expected ?? true)}
                          onChange={(e) => {
                            const val = e.target.value;
                            const boolVal = val.toLowerCase() === 'true' ? true : (val.toLowerCase() === 'false' ? false : val);
                            setEditingTool({
                              ...tool,
                              otp_config: {
                                ...(tool.otp_config || {}),
                                verify_success: {
                                  ...(tool.otp_config?.verify_success || {}),
                                  expected: boolVal
                                }
                              }
                            });
                          }}
                          className="w-full px-3 py-2 text-xs border border-gray-200 rounded-xl bg-white font-mono font-bold text-emerald-600 h-[38px]"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderMcpView = () => {
    return (
      <div className="space-y-8 animate-fadeIn">
        {/* Discovery & Add Section */}
        <div className="bg-white rounded-[32px] border border-gray-100 p-8 shadow-sm">
          {!isAddingMcp ? (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="w-16 h-16 bg-gray-900 text-white rounded-2xl flex items-center justify-center mb-4 shadow-xl shadow-gray-200">
                <Cpu size={32} />
              </div>
              <h3 className="text-xl font-black text-gray-900 tracking-tight mb-2">Connect New Neural Extension</h3>
              <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-6">Discovery and integrate third-party MCP protocols</p>
              <button
                onClick={() => { setIsAddingMcp(true); setUiError(null); }}
                className="flex items-center gap-2 px-8 py-3 bg-gray-900 text-white rounded-xl hover:bg-black transition-all font-bold text-xs uppercase tracking-widest shadow-lg shadow-gray-200"
              >
                <Plus size={16} />
                Discover Server
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-black text-gray-900 tracking-tight">MCP Discovery</h3>
                <button onClick={() => { setIsAddingMcp(false); setMcpDiscovery({ url: "", headers: "{}", tools: [] }); setUiError(null); }} className="p-2 text-gray-400 hover:text-gray-900"><X size={20} /></button>
              </div>

              {uiError && (
                <div className="p-4 bg-red-50 border border-red-100 rounded-2xl animate-shake">
                  <p className="text-xs font-bold text-red-600 uppercase tracking-widest flex items-center gap-2">
                    <AlertCircle size={14} /> {uiError}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Server URL (SSE)</label>
                  <input
                    value={mcpDiscovery.url}
                    onChange={(e) => setMcpDiscovery(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="https://example.com/mcp"
                    className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-8 focus:ring-gray-900/5 focus:border-gray-900 transition-all font-medium"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Custom Headers (JSON)</label>
                  <input
                    value={mcpDiscovery.headers}
                    onChange={(e) => setMcpDiscovery(prev => ({ ...prev, headers: e.target.value }))}
                    placeholder='{"Authorization": "BearerToken"}'
                    className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-8 focus:ring-gray-900/5 focus:border-gray-900 transition-all font-mono text-[11px]"
                  />
                </div>
              </div>

              <div className="flex justify-center pt-4">
                <button
                  onClick={handleDiscoverMcp}
                  disabled={isDiscovering || !mcpDiscovery.url}
                  className="flex items-center gap-2 px-8 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all font-bold text-xs uppercase tracking-widest disabled:opacity-50"
                >
                  {isDiscovering ? (
                    <RotateCcw size={16} className="animate-spin" />
                  ) : (
                    <Search size={16} />
                  )}
                  {isDiscovering ? "Discovering..." : "Scan for Tools"}
                </button>
              </div>

              {mcpDiscovery.tools.length > 0 && (
                <div className="mt-8 pt-8 border-t border-gray-50 animate-fadeIn">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-sm font-black text-gray-900 uppercase tracking-widest">Available Extentions ({mcpDiscovery.tools.length})</h4>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Toggle to include in synchronization</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {mcpDiscovery.tools.map((tool, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100 hover:bg-white transition-colors group min-h-[64px]">
                        <div className="flex items-center gap-3 min-w-0 flex-1 mr-4 py-1">
                          <Layers size={14} className="text-gray-400 flex-shrink-0" />
                          <span className="text-xs font-black text-gray-900 break-all whitespace-normal">{tool.tool_name}</span>
                        </div>
                        <button
                          onClick={() => {
                            const newTools = [...mcpDiscovery.tools];
                            newTools[idx].is_active = !newTools[idx].is_active;
                            setMcpDiscovery(prev => ({ ...prev, tools: newTools }));
                          }}
                          className={`w-11 h-6 rounded-full border-2 transition-all relative bg-white ${tool.is_active ? "border-[#83bbe1]" : "border-gray-200"}`}
                        >
                          <div className={`absolute top-[2px] w-4 h-4 rounded-full transition-all ${tool.is_active ? "left-[22px] bg-[#83bbe1]" : "left-[2px] bg-gray-300"}`} />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end pt-8">
                    <button
                      onClick={handleSaveMcp}
                      className="px-10 py-3 bg-gray-900 text-white rounded-xl hover:bg-black transition-all font-bold text-xs uppercase tracking-widest shadow-lg shadow-gray-200"
                    >
                      Synchronize Extension
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Servers List */}
        <div className="space-y-6">
          <h2 className="text-lg font-black text-gray-900 tracking-tight flex items-center gap-3">
            <Database size={20} className="text-gray-400" />
            Active Neural Connections
          </h2>

          <div className="grid grid-cols-1 gap-6">
            {mcpServers.map((server) => (
              <div key={server.id} className="bg-white rounded-[32px] border border-gray-100 p-8 shadow-sm group">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 pb-8 border-b border-gray-50">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${server.is_active ? 'bg-gray-900 text-white shadow-xl shadow-gray-200' : 'bg-gray-50 text-gray-400'}`}>
                      <Cpu size={24} />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <h3 className="text-base font-black text-gray-900">{server.url}</h3>
                        <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${server.is_active ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-gray-50 text-gray-400 border-gray-100'}`}>
                          {server.is_active ? 'Active' : 'Standby'}
                        </span>
                      </div>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Established: {new Date(server.created_at || Date.now()).toLocaleDateString()}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleMcpServer(server.id, server.is_active)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all font-bold text-[10px] uppercase tracking-widest border ${server.is_active
                          ? "bg-gray-900 text-white border-gray-900 hover:bg-gray-800"
                          : "bg-white text-gray-900 border-gray-100 hover:bg-gray-50"
                        }`}
                    >
                      {server.is_active ? 'Suspend' : 'Resume'}
                    </button>
                    <button
                      onClick={() => deleteMcpServer(server.id)}
                      className="p-2.5 text-gray-400 bg-white border border-gray-100 hover:text-red-500 hover:bg-red-50 hover:border-red-100 rounded-xl transition-all shadow-sm flex items-center justify-center"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {(server.tools || []).map((tool) => (
                    <div key={tool.id} className="flex items-center justify-between px-4 py-3 bg-gray-50/50 rounded-xl border border-gray-50 min-w-0 min-h-[48px]">
                      <span className={`text-[11px] font-bold break-all whitespace-normal mr-3 flex-1 py-1 ${tool.is_active ? 'text-gray-900' : 'text-gray-400'}`}>
                        {tool.tool_name}
                      </span>
                      <button
                        onClick={() => toggleMcpTool(tool.id, tool.is_active)}
                        className={`w-11 h-6 rounded-full border-2 transition-all relative bg-white ${tool.is_active ? "border-[#83bbe1]" : "border-gray-200"}`}
                      >
                        <div className={`absolute top-[2px] w-4 h-4 rounded-full transition-all ${tool.is_active ? "left-[22px] bg-[#83bbe1]" : "left-[2px] bg-gray-300"}`} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {mcpServers.length === 0 && (
              <div className="text-center py-20 bg-gray-50/50 rounded-[40px] border border-dashed border-gray-200">
                <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest">No neural extensions mapped to this agent</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderToolsList = () => {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Neural Extensions"
          description="Equip your agents with sophisticated behavioral tools and external sensory protocols."
          actions={
            <>
              <div className="relative group">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-gray-900 transition-colors" />
                <input
                  type="text"
                  placeholder="Search tools..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-11 pr-4 py-2.5 bg-white border border-gray-100 rounded-xl text-xs focus:outline-none focus:ring-4 focus:ring-gray-900/5 focus:border-gray-900 transition-all w-[240px] font-medium shadow-sm hover:border-gray-200"
                />
              </div>

              {!isMcpMode && (
                <button
                  onClick={() => {
                    setEditingTool(null);
                    setIsAdding(true);
                    setIsJsonMode(false);
                    setUiError(null);
                  }}
                  className="flex items-center gap-2 px-8 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-black transition-all font-black text-sm uppercase shadow-xl shadow-gray-200 border border-transparent active:scale-95"
                >
                  <Plus size={18} /> Add Capability
                </button>
              )}

              <button
                onClick={() => {
                  setIsMcpMode(!isMcpMode);
                  if (!isMcpMode) fetchMcpServers();
                }}
                className="flex items-center gap-2 px-8 py-3 rounded-xl transition-all font-bold text-xs uppercase tracking-widest shadow-lg active:scale-95 bg-gray-900 text-white hover:bg-black shadow-gray-950/20"
              >
                <Cpu size={16} /> {isMcpMode ? "Capability Hub" : "MCP Hub"}
              </button>
            </>
          }
        />

        {isMcpMode ? renderMcpView() : (
          <>
            {filteredTools.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTools.map((tool) => (
                  <div
                    key={tool.id}
                    className="group bg-white rounded-[32px] border border-gray-100 p-6 hover:shadow-2xl hover:shadow-gray-200/50 hover:-translate-y-1 transition-all duration-300 relative overflow-hidden flex flex-col h-full"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter border ${getMethodColor(tool.method)}`}>
                        {tool.method}
                      </span>
                      <div
                        onClick={() => handleToggle(tool)}
                        className={`w-11 h-6 rounded-full border-2 transition-all relative bg-white cursor-pointer ${tool.is_active ? "border-[#83bbe1]" : "border-gray-200"}`}
                      >
                        <div className={`absolute top-[2px] w-4 h-4 rounded-full transition-all ${tool.is_active ? "left-[22px] bg-[#83bbe1]" : "left-[2px] bg-gray-300"}`} />
                      </div>
                    </div>

                    <div className="mb-6 flex-grow">
                      <h3 className="text-xs font-bold uppercase tracking-widest text-gray-900 mb-2 truncate transition-colors uppercase tracking-tight">{tool.name}</h3>
                      <p className="text-gray-500 text-xs font-medium leading-relaxed line-clamp-3 mb-4">{tool.description}</p>
                      <div className="flex items-center gap-2 text-gray-400 bg-gray-50 p-2 rounded-xl border border-gray-100/50">
                        <Globe size={12} className="flex-shrink-0" />
                        <span className="text-[10px] font-bold truncate">{getHostname(tool.url)}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 py-4 border-t border-gray-50 mb-2">
                      <div className="flex flex-col">
                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Identifiers</span>
                        <span className="text-sm font-black text-gray-900">{(tool.parameters || []).length}</span>
                      </div>
                      <div className="w-[1px] h-6 bg-gray-100" />
                      <div className="flex flex-col">
                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Headers</span>
                        <span className="text-sm font-black text-gray-900">
                          {(() => {
                            try {
                              const h = typeof tool.headers === 'string' ? JSON.parse(tool.headers) : (tool.headers || {});
                              return Object.keys(h).length;
                            } catch { return "!"; }
                          })()}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-4 mt-auto">
                      <button
                        onClick={() => handleEdit(tool)}
                        className="flex-1 flex items-center justify-center gap-2 py-2 bg-gray-50 text-gray-900 rounded-xl hover:bg-gray-900 hover:text-white transition-all font-bold text-[10px] uppercase tracking-widest"
                      >
                        <Edit2 size={12} /> Configure
                      </button>
                      <button
                        onClick={() => handleDelete(tool.id)}
                        className="p-2.5 bg-white border border-gray-100 text-gray-400 rounded-xl hover:bg-red-50 hover:text-red-600 hover:border-red-100 transition-all shadow-sm flex items-center justify-center"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[40px] border border-gray-100 shadow-sm animate-fadeIn">
                <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-8">No external capabilities configured</p>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn border-t border-gray-100">
      <div className="w-full max-w-6xl mx-auto space-y-6">
        {isAdding || editingTool ? renderEditor() : renderToolsList()}

        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={confirmDelete}
          isLoading={isDeleting}
          title="Delete Neural Extension?"
          message="This action will permanently dismantle this functional capability from the AI ecosystem. Are you prepared to proceed with the deletion?"
        />

        <DeleteConfirmModal
          isOpen={mcpServerToDelete !== null}
          onClose={() => setMcpServerToDelete(null)}
          onConfirm={confirmDeleteMcp}
          isLoading={isDeletingMcp}
          title="Dismantle Neural Connection?"
          message="This will sever all functional links to this MCP server and its constituent protocols. This action is irreversible. Proceed?"
        />

        {/* 🚀 TEST MODAL */}
        {isTestModalOpen && ReactDOM.createPortal(
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-gray-900/60 backdrop-blur-xl animate-fadeIn"
              onClick={() => !isTestLoading && setIsTestModalOpen(false)}
            />

            <div className="relative w-full max-w-6xl bg-white rounded-[32px] shadow-2xl border border-gray-100 overflow-hidden animate-slideUp flex flex-col min-h-[600px] max-h-[90vh]">
              {/* Header */}
              <div className="px-10 py-8 border-b border-gray-50 flex items-center justify-between bg-white relative z-10">
                <div className="space-y-1">
                  <h3 className="text-xl font-black text-gray-900 tracking-tight">Test Tool</h3>
                  <p className="text-[11px] text-gray-400 font-bold uppercase tracking-widest">Test the tool execution with custom parameters.</p>
                </div>
                <button
                  onClick={() => setIsTestModalOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-900 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[45%_55%] overflow-auto bg-white">
                {/* Left Side: Parameters Grouped */}
                <div className="h-full overflow-y-auto bg-white border-r border-gray-50 p-10">
                  {(() => {
                    const renderParam = (param, parentPath = []) => {
                      const fullPathArr = [...parentPath, param.name];
                      const fullPath = fullPathArr.join('.');
                      if (param.type === 'object') {
                        const subProperties = param.properties || [];
                        return (
                          <div key={fullPath} className="space-y-4 border-l-2 border-gray-100 pl-4 py-2 my-2 bg-gray-50/20 rounded-r-xl">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-black text-gray-600 uppercase tracking-widest">
                                {param.name} (Object)
                              </span>
                            </div>
                            {param.description && (
                              <p className="text-[9px] text-gray-400 font-medium">{param.description}</p>
                            )}
                            <div className="space-y-4">
                              {subProperties.map(subParam => renderParam(subParam, fullPathArr))}
                            </div>
                          </div>
                        );
                      }

                      if (param.type === 'array') {
                        const subProperties = param.items?.properties || [];
                        const length = arrayLengths[fullPath] || 0;
                        const indexes = Array.from({ length }, (_, i) => i);

                        return (
                          <div key={fullPath} className="space-y-4 border-l-2 border-gray-100 pl-4 py-2 my-2 bg-gray-50/20 rounded-r-xl">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-black text-gray-600 uppercase tracking-widest">
                                {param.name} (Array)
                              </span>
                            </div>
                            {param.description && (
                              <p className="text-[9px] text-gray-400 font-medium">{param.description}</p>
                            )}
                            <div className="space-y-6">
                              {indexes.map((idx) => (
                                <div key={idx} className="border border-dashed border-gray-200 p-4 rounded-xl relative space-y-4">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[9px] font-bold text-gray-400 uppercase">Item #{idx + 1}</span>
                                  </div>
                                  <div className="space-y-4">
                                    {subProperties.map(subParam => renderParam(subParam, [...fullPathArr, idx]))}
                                    {subProperties.length === 0 && (
                                      <input
                                        placeholder={`Value for item #${idx + 1}`}
                                        className="w-full px-5 py-3.5 border rounded-2xl text-[13px] font-bold bg-gray-50 border-gray-100 text-gray-700 placeholder-gray-300 focus:outline-none focus:ring-4 focus:ring-gray-900/5 focus:border-gray-900"
                                        value={testInputs[`${fullPath}.${idx}`] || ''}
                                        onChange={(e) => {
                                          setTestInputs({ ...testInputs, [`${fullPath}.${idx}`]: e.target.value });
                                        }}
                                      />
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={fullPath} className="space-y-2">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">
                              {fullPath}
                            </label>
                            {param.value_type === 'constant' && (
                              <span className="text-[9px] font-black bg-blue-50 text-blue-600 px-2.5 py-1 rounded-lg uppercase tracking-widest border border-blue-100/50">
                                Fixed Value
                              </span>
                            )}
                          </div>
                          <input
                            placeholder={param.name}
                            readOnly={param.value_type === 'constant'}
                            className={`w-full px-5 py-3.5 border rounded-2xl text-[13px] font-bold transition-all ${param.value_type === 'constant'
                                ? "bg-gray-100/50 border-gray-100 text-gray-400 cursor-not-allowed select-none"
                                : "bg-gray-50 border-gray-100 text-gray-700 placeholder-gray-300 focus:outline-none focus:ring-4 focus:ring-gray-900/5 focus:border-gray-900"
                              }`}
                            value={testInputs[fullPath] || ''}
                            onChange={(e) => {
                              if (param.value_type !== 'constant') {
                                setTestInputs({ ...testInputs, [fullPath]: e.target.value });
                              }
                            }}
                          />
                          {param.description && (
                            <p className="text-[9px] text-gray-400 font-medium ml-1">{param.description}</p>
                          )}
                        </div>
                      );
                    };

                    return ['path', 'query', 'body'].map((loc) => {
                      const locParams = (editingTool?.parameters || []).filter(p => p.name && (p.location || 'body') === loc);
                      if (locParams.length === 0) return null;

                      return (
                        <div key={loc} className="mb-10 last:mb-0">
                          <div className="space-y-1 mb-6">
                            <h4 className="text-[11px] font-black text-gray-900 uppercase tracking-widest">
                              {loc === 'query' ? 'Query Parameters' : loc === 'path' ? 'Path Parameters' : 'Body Parameters'}
                            </h4>
                            <p className="text-[9px] text-gray-400 font-bold uppercase tracking-tighter">
                              {loc === 'query' ? 'Query string parameters for the request.' : loc === 'path' ? 'Dynamic URL path variables.' : 'JSON payload parameters for the request body.'}
                            </p>
                          </div>

                          <div className="space-y-6">
                            {locParams.map((param) => renderParam(param))}
                          </div>
                        </div>
                      );
                    });
                  })()}

                  {(editingTool?.parameters || []).filter(p => p.name).length === 0 && (
                    <div className="h-full flex flex-col items-center justify-center py-20 text-center">
                      <div className="w-16 h-16 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
                        <CheckCircle2 size={32} />
                      </div>
                      <p className="text-sm font-bold text-gray-900 uppercase tracking-widest">Ready for Pulse</p>
                      <p className="text-xs text-gray-400 mt-2">No manual inputs required for this capability.</p>
                    </div>
                  )}
                </div>

                {/* Right Side: Result Section */}
                <div className="h-full overflow-y-auto bg-gray-50/30 p-10 pb-40">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-[11px] font-black text-gray-900 uppercase tracking-widest">Result</h4>
                    <div className="flex items-center gap-3">
                      {testResult && (
                        <div className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest flex items-center gap-2 border shadow-sm ${testResult.status_code >= 200 && testResult.status_code < 300
                            ? "bg-blue-50 text-blue-600 border-blue-100"
                            : "bg-red-50 text-red-600 border-red-100"
                          }`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${testResult.status_code >= 200 && testResult.status_code < 300 ? "bg-blue-500" : "bg-red-500"} animate-pulse`} />
                          {testResult.status_code >= 200 && testResult.status_code < 300 ? "Success" : "Error"}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={`rounded-2xl shadow-xl flex flex-col mb-12 border transition-all duration-500 ${testResult?.status_code >= 200 && testResult?.status_code < 300
                      ? "bg-blue-50 border-blue-200"
                      : testResult?.status_code >= 400
                        ? "bg-red-50 border-red-200"
                        : "bg-white border-gray-100"
                    }`}>
                    {testResult ? (
                      <div className="p-8">
                        <pre className="text-[12px] font-mono text-gray-700 leading-relaxed whitespace-pre-wrap break-all">
                          {JSON.stringify(testResult.data || testResult, null, 2)}
                        </pre>
                      </div>
                    ) : (
                      <div className="flex-1 flex flex-col items-center justify-center opacity-20 py-20">
                        <Database size={48} className="text-gray-400 mb-4" />
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Execution Required</p>
                      </div>
                    )}
                    {testResult && (
                      <div className="p-4 bg-gray-50/50 border-t border-gray-50 flex justify-end">
                        <button
                          onClick={() => {
                            const text = JSON.stringify(testResult.data || testResult, null, 2);
                            navigator.clipboard.writeText(text);
                          }}
                          className="p-2 text-gray-400 hover:text-gray-900 transition-colors"
                        >
                          <Code size={16} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Raw Request Accordion */}
                  <div className="mt-8 border-t border-gray-100 pt-8">
                    <div
                      onClick={() => setIsRawRequestOpen(!isRawRequestOpen)}
                      className="flex items-center justify-between p-4 bg-white border border-gray-100 rounded-2xl cursor-pointer hover:bg-gray-50 transition-all group"
                    >
                      <span className="text-[10px] font-black text-gray-900 uppercase tracking-widest">Raw Request</span>
                      <div className="flex items-center gap-3">
                        {isRawRequestOpen && testResult?.curl_command && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(testResult.curl_command);
                            }}
                            className="flex items-center gap-2 p-1.5 hover:bg-gray-100 rounded-md text-gray-400 hover:text-gray-900 transition-all border border-transparent hover:border-gray-200"
                            title="Copy as CURL"
                          >
                            <Copy size={12} />
                          </button>
                        )}
                        <ChevronDown size={14} className={`text-gray-400 transition-transform duration-300 ${isRawRequestOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </div>

                    {isRawRequestOpen && (
                      <div className="mt-4 animate-fadeIn">
                        <div className="bg-gray-900 rounded-2xl p-6 overflow-x-auto border border-gray-800 shadow-2xl">
                          <pre className="text-[11px] font-mono text-gray-300 leading-relaxed whitespace-pre">
                            {testResult?.raw_request || "No request data available. Execute a pulse to see the raw request."}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Sticky Footer */}
              <div className="px-10 py-6 border-t border-gray-50 bg-white flex items-center justify-end gap-4 shadow-[0_-4px_24px_rgba(0,0,0,0.02)]">
                <button
                  onClick={() => setIsTestModalOpen(false)}
                  className="px-8 py-2.5 text-[11px] font-black text-gray-500 uppercase tracking-widest hover:text-gray-900 transition-all"
                >
                  Cancel
                </button>
                <button
                  disabled={isTestLoading}
                  onClick={async () => {
                    setIsTestLoading(true);
                    try {
                      // Convert flat dot-notation inputs back to nested structure
                      const nestedInputs = {};
                      Object.entries(testInputs).forEach(([key, val]) => {
                        const parts = key.split('.');
                        let current = nestedInputs;
                        for (let i = 0; i < parts.length; i++) {
                          const part = parts[i];
                          const nextPart = parts[i + 1];
                          const isNextPartIndex = nextPart !== undefined && /^\d+$/.test(nextPart);

                          const isCurrentArray = Array.isArray(current);
                          const keyOrIdx = isCurrentArray ? parseInt(part, 10) : part;

                          if (i === parts.length - 1) {
                            current[keyOrIdx] = val;
                          } else {
                            const isNextPartArray = isNextPartIndex;
                            if (!current[keyOrIdx]) {
                              current[keyOrIdx] = isNextPartArray ? [] : {};
                            }
                            current = current[keyOrIdx];
                          }
                        }
                      });

                      const res = await apiClient.testTool(editingTool.id, nestedInputs);
                      setTestResult(res);
                    } catch (err) {
                      setTestResult({ error: err.message, status_code: 500 });
                    } finally {
                      setIsTestLoading(false);
                    }
                  }}
                  className="px-10 py-2.5 bg-gray-900 text-white rounded-xl font-black text-[11px] uppercase tracking-widest shadow-2xl shadow-gray-200 hover:bg-black transition-all active:scale-95 disabled:opacity-50 flex items-center gap-3"
                >
                  {isTestLoading && <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />}
                  {isTestLoading ? "Running..." : "Run"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default ApiToolsManagement;
